// src/components/RequireFeature.tsx
//
// Route-level (or section-level) tier gate. Renders `children` when the merchant's
// tier includes `feature`, otherwise the UpgradeGate. Used to wrap gated /app routes
// in App.tsx — a locked page never mounts (no doomed queries, no rules-of-hooks issues
// from per-page early returns).
//
// UI gating only; the backend enforces every gated route independently (403
// FEATURE_NOT_IN_TIER), so this can never be the sole line of defense.

import { type ReactNode } from "react";
import { useTier } from "@/hooks/useTier";
import UpgradeGate from "@/components/UpgradeGate";
import { type Feature } from "@/lib/tier";

interface RequireFeatureProps {
  feature: Feature;
  description?: string;
  highlights?: string[];
  children: ReactNode;
}

export default function RequireFeature({
  feature,
  description,
  highlights,
  children,
}: RequireFeatureProps) {
  const { hasFeature, loading } = useTier();

  // Only show the gate once the tier has resolved. In practice the merchant
  // profile is already loaded before any /app page renders (AuthProvider gates
  // the app), so this just guards the theoretical pre-load tick.
  if (!loading && !hasFeature(feature)) {
    return (
      <UpgradeGate
        feature={feature}
        description={description}
        highlights={highlights}
      />
    );
  }
  return <>{children}</>;
}
